import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Table, Alert, ListGroup, Pagination, Badge, ButtonGroup, InputGroup } from 'react-bootstrap';
import { materialsAPI, vatAPI } from '../utils/api';
import { getVatPreference, saveVatPreference } from '../utils/vatPreferences';

const OrderForm = ({
  initialSelectedMaterials = [],
  initialNote = '',
  initialDiscountVat = 0,
  initialDiscountNonVat = 0,
  initialMarkupPercent = 0,
  showMarkupInput = false,
  onSubmit,
  loading = false,
  submitButtonText = 'ส่งคำขอสั่งซื้อ',
  submitButtonIcon = 'bi-send',
  allowPriceEdit = true,
  allowQuantityEdit = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState(initialSelectedMaterials);
  const [note, setNote] = useState(initialNote);
  const [discountVat, setDiscountVat] = useState(initialDiscountVat);
  const [discountNonVat, setDiscountNonVat] = useState(initialDiscountNonVat);
  const [markupPercent, setMarkupPercent] = useState(initialMarkupPercent);

  // Track quantities for search results and browse materials
  const [pendingQuantities, setPendingQuantities] = useState({});

  // Material list state
  const [allMaterials, setAllMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [searchTimeout, setSearchTimeout] = useState(null);
  const [formError, setFormError] = useState('');

  // VAT state
  const [vatRate, setVatRate] = useState(7); // Default 7%

  // Track if we've initialized to prevent resetting state
  const isInitialized = useRef(false);

  // Load all materials and VAT rate on mount
  useEffect(() => {
    loadAllMaterials();
    loadVatRate();
  }, []);

  // Update internal state when initial values change (only for edit mode)
  useEffect(() => {
    if (initialSelectedMaterials.length > 0 && !isInitialized.current) {
      setSelectedMaterials(initialSelectedMaterials);
      isInitialized.current = true;
    }
  }, [initialSelectedMaterials]);

  useEffect(() => {
    if (initialNote && !isInitialized.current) {
      setNote(initialNote);
    }
  }, [initialNote]);

  useEffect(() => {
    if (initialDiscountVat !== undefined) {
      setDiscountVat(initialDiscountVat);
    }
  }, [initialDiscountVat]);

  useEffect(() => {
    if (initialDiscountNonVat !== undefined) {
      setDiscountNonVat(initialDiscountNonVat);
    }
  }, [initialDiscountNonVat]);

  useEffect(() => {
    if (initialMarkupPercent !== undefined) {
      setMarkupPercent(initialMarkupPercent);
    }
  }, [initialMarkupPercent]);

  const loadAllMaterials = async () => {
    try {
      setMaterialsLoading(true);
      // Get materials sorted by order frequency (most ordered first)
      const res = await materialsAPI.getAllSortedByFrequency();
      setAllMaterials(res.data.data);
    } catch (err) {
      console.error('Error loading materials:', err);
    } finally {
      setMaterialsLoading(false);
    }
  };

  const loadVatRate = async () => {
    try {
      const res = await vatAPI.get();
      setVatRate(parseFloat(res.data.data.rate));
    } catch (err) {
      console.error('Error loading VAT rate:', err);
      // Keep default 7% if fails
    }
  };

  const handleSearchMaterial = async (query) => {
    setSearchQuery(query);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await materialsAPI.search(query);
        setSearchResults(res.data.data);
      } catch (err) {
        console.error('Error searching materials:', err);
      }
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleSelectMaterial = (material, quantity = 1) => {
    // Check if already added
    if (selectedMaterials.find(m => m.materialId === material.id)) {
      alert('วัตถุดิบนี้ถูกเพิ่มแล้ว');
      return;
    }

    // Check if there's a saved VAT preference for this material
    const savedVatPreference = getVatPreference(material.id);
    const hasVat = savedVatPreference !== null ? savedVatPreference : false;

    setSelectedMaterials([
      ...selectedMaterials,
      {
        materialId: material.id,
        materialCode: material.code,
        materialName: material.name,
        unit: material.unit,
        quantity: parseFloat(quantity) || 1,
        unitCost: material.salePrice,
        note: '',
        hasVat: hasVat
      }
    ]);

    // Clear the pending quantity for this material
    setPendingQuantities(prev => {
      const updated = { ...prev };
      delete updated[material.id];
      return updated;
    });

    setSearchQuery('');
    setSearchResults([]);
  };

  const handleUpdatePendingQuantity = (materialId, quantity) => {
    setPendingQuantities(prev => ({
      ...prev,
      [materialId]: parseFloat(quantity) || 1
    }));
  };

  const getPendingQuantity = (materialId) => {
    return pendingQuantities[materialId] || 1;
  };

  const handleUpdateQuantity = (index, quantity) => {
    const updated = [...selectedMaterials];
    updated[index].quantity = parseFloat(quantity) || 0;
    setSelectedMaterials(updated);
  };

  const handleUpdateCost = (index, cost) => {
    const updated = [...selectedMaterials];
    updated[index].unitCost = parseFloat(cost) || 0;
    setSelectedMaterials(updated);
  };

  const handleUpdateNote = (index, note) => {
    const updated = [...selectedMaterials];
    updated[index].note = note;
    setSelectedMaterials(updated);
  };

  const handleUpdateVat = (index, hasVat) => {
    const updated = [...selectedMaterials];
    updated[index].hasVat = hasVat;
    setSelectedMaterials(updated);

    // Save VAT preference to cookies for future orders
    const materialId = updated[index].materialId;
    if (materialId) {
      saveVatPreference(materialId, hasVat);
    }
  };

  const handleRemoveMaterial = (index) => {
    setSelectedMaterials(selectedMaterials.filter((_, i) => i !== index));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unitCost;
    if (item.hasVat) {
      return subtotal * (1 + vatRate / 100);
    }
    return subtotal;
  };

  const calculateSubtotalVat = () => {
    return selectedMaterials.reduce((sum, item) => {
      if (item.hasVat) {
        return sum + calculateItemTotal(item);
      }
      return sum;
    }, 0);
  };

  const calculateSubtotalNonVat = () => {
    return selectedMaterials.reduce((sum, item) => {
      if (!item.hasVat) {
        return sum + calculateItemTotal(item);
      }
      return sum;
    }, 0);
  };

  const calculateTotal = () => {
    const vatSubtotal = calculateSubtotalVat();
    const nonVatSubtotal = calculateSubtotalNonVat();

    const clampedDiscountVat = Math.min(parseFloat(discountVat) || 0, vatSubtotal);
    const clampedDiscountNonVat = Math.min(parseFloat(discountNonVat) || 0, nonVatSubtotal);

    const baseTotal = Math.max(0, vatSubtotal - clampedDiscountVat) + Math.max(0, nonVatSubtotal - clampedDiscountNonVat);
    return baseTotal * (1 + (parseFloat(markupPercent) || 0) / 100);
  };

  const calculateTotalVat = () => {
    return selectedMaterials.reduce((sum, item) => {
      if (item.hasVat) {
        const subtotal = item.quantity * item.unitCost;
        return sum + (subtotal * vatRate / 100);
      }
      return sum;
    }, 0);
  };

  // Get unique categories from all materials
  const getCategories = () => {
    const categories = new Set();
    allMaterials.forEach(material => {
      if (material.category) {
        categories.add(material.category);
      }
    });
    return Array.from(categories).sort();
  };

  // Filter materials by category
  const getFilteredMaterials = () => {
    if (selectedCategory === 'all') {
      return allMaterials;
    }
    return allMaterials.filter(material => material.category === selectedCategory);
  };

  // Pagination logic for material list
  const filteredMaterials = getFilteredMaterials();
  const indexOfLastMaterial = currentPage * itemsPerPage;
  const indexOfFirstMaterial = indexOfLastMaterial - itemsPerPage;
  const currentMaterials = filteredMaterials.slice(indexOfFirstMaterial, indexOfLastMaterial);
  const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when changing category
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    const hasInvalidQty = selectedMaterials.some(m => !(parseFloat(m.quantity) > 0));
    if (hasInvalidQty) {
      setFormError('กรุณากรอกจำนวนให้ครบทุกรายการ (ต้องมากกว่า 0)');
      return;
    }
    onSubmit({
      selectedMaterials,
      note,
      discountVat: parseFloat(discountVat) || 0,
      discountNonVat: parseFloat(discountNonVat) || 0,
      markupPercent: parseFloat(markupPercent) || 0
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Materials */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-box me-2"></i>
            รายการวัตถุดิบ
          </h5>
        </Card.Header>
        <Card.Body>
          {/* Search Materials — hidden when quantity is locked (fulfilled order) */}
          {allowQuantityEdit && (
          <Form.Group className="mb-3">
            <Form.Label>ค้นหาวัตถุดิบ</Form.Label>
            <Form.Control
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchMaterial(e.target.value)}
              placeholder="ค้นหาด้วย รหัส, ชื่อ หรือ บาร์โค้ด..."
            />
          </Form.Group>
          )}

          {allowQuantityEdit && searchResults.length > 0 && (
            <ListGroup className="mb-3">
              {searchResults.map(material => {
                const isAdded = selectedMaterials.find(m => m.materialId === material.id);
                const currentQuantity = getPendingQuantity(material.id);

                return (
                  <ListGroup.Item key={material.id} className="material-search-result">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <strong>{material.code}</strong> - {material.name}
                        <br />
                        <small className="text-muted">
                          หน่วย: {material.unit} | ราคา: {material.salePrice} บาท
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <InputGroup size="sm" style={{ width: '150px' }}>
                          <Button
                            variant="outline-secondary"
                            onClick={() => handleUpdatePendingQuantity(material.id, Math.max(0.0001, currentQuantity - 1))}
                            disabled={currentQuantity <= 0.0001}
                          >
                            <i className="bi bi-dash"></i>
                          </Button>
                          <Form.Control
                            type="number"
                            min="0.0001"
                            step="0.0001"
                            value={currentQuantity}
                            onChange={(e) => handleUpdatePendingQuantity(material.id, e.target.value)}
                            className="text-center"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => handleUpdatePendingQuantity(material.id, currentQuantity + 1)}
                          >
                            <i className="bi bi-plus"></i>
                          </Button>
                        </InputGroup>
                        <Button
                          variant={isAdded ? "outline-secondary" : "primary"}
                          size="sm"
                          onClick={() => handleSelectMaterial(material, currentQuantity)}
                          disabled={isAdded}
                          style={{ minWidth: '80px' }}
                        >
                          {isAdded ? (
                            <>
                              <i className="bi bi-check"></i> เพิ่มแล้ว
                            </>
                          ) : (
                            <>
                              <i className="bi bi-plus-circle"></i> เพิ่ม
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}

          {/* Browse All Materials — hidden when quantity is locked */}
          {allowQuantityEdit && searchQuery.length === 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">หรือเลือกจากรายการทั้งหมด (เรียงตามที่สั่งบ่อย)</h6>
                <div>
                  <Badge bg="info" className="me-1">
                    <i className="bi bi-star-fill me-1"></i>
                    เรียงตามยอดนิยม
                  </Badge>
                  <Badge bg="secondary">{allMaterials.length} รายการ</Badge>
                </div>
              </div>

              {/* Category Filter Buttons */}
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <small className="text-muted me-2">กรองตามหมวดหมู่:</small>
                </div>
                <ButtonGroup size="sm" className="flex-wrap">
                  <Button
                    variant={selectedCategory === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => handleCategoryChange('all')}
                  >
                    <i className="bi bi-grid-3x3-gap me-1"></i>
                    ทั้งหมด ({allMaterials.length})
                  </Button>
                  {getCategories().map(category => {
                    const count = allMaterials.filter(m => m.category === category).length;
                    return (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? 'primary' : 'outline-primary'}
                        onClick={() => handleCategoryChange(category)}
                      >
                        {category} ({count})
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </div>

              {materialsLoading ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-primary"></div>
                  <p className="mt-2 mb-0 text-muted small">กำลังโหลดวัตถุดิบ...</p>
                </div>
              ) : filteredMaterials.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bi bi-filter-circle" style={{ fontSize: '2rem', color: '#999' }}></i>
                  <p className="text-muted mt-2 mb-0">ไม่พบวัตถุดิบในหมวดหมู่นี้</p>
                </div>
              ) : (
                <>
                  <Table hover className="mb-3">
                    <thead className="table-light">
                      <tr>
                        <th>รหัส</th>
                        <th>ชื่อ</th>
                        <th>หน่วย</th>
                        <th>ราคา</th>
                        <th>หมวดหมู่</th>
                        <th style={{ width: '180px' }}>จำนวน</th>
                        <th className="text-center">เพิ่ม</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentMaterials.map(material => {
                        const isAdded = selectedMaterials.find(m => m.materialId === material.id);
                        const orderFrequency = material.orderFrequency || 0;
                        const isFrequentlyOrdered = orderFrequency > 0;
                        const currentQuantity = getPendingQuantity(material.id);

                        return (
                          <tr key={material.id} className={isFrequentlyOrdered ? 'table-info' : ''}>
                            <td>
                              <code>{material.code}</code>
                              {isFrequentlyOrdered && (
                                <Badge bg="warning" className="ms-1" title={`สั่ง ${orderFrequency} ครั้ง`}>
                                  <i className="bi bi-star-fill"></i>
                                </Badge>
                              )}
                            </td>
                            <td>{material.name}</td>
                            <td>{material.unit}</td>
                            <td>{material.salePrice.toFixed(2)} ฿</td>
                            <td><small className="text-muted">{material.category || '-'}</small></td>
                            <td>
                              <InputGroup size="sm">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => handleUpdatePendingQuantity(material.id, Math.max(0.0001, currentQuantity - 1))}
                                  disabled={currentQuantity <= 0.0001}
                                >
                                  <i className="bi bi-dash"></i>
                                </Button>
                                <Form.Control
                                  type="number"
                                  min="0.0001"
                                  step="0.0001"
                                  value={currentQuantity}
                                  onChange={(e) => handleUpdatePendingQuantity(material.id, e.target.value)}
                                  className="text-center"
                                />
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => handleUpdatePendingQuantity(material.id, currentQuantity + 1)}
                                >
                                  <i className="bi bi-plus"></i>
                                </Button>
                              </InputGroup>
                            </td>
                            <td className="text-center">
                              <Button
                                type="button"
                                variant={isAdded ? "outline-secondary" : "primary"}
                                size="sm"
                                onClick={() => handleSelectMaterial(material, currentQuantity)}
                                disabled={isAdded}
                              >
                                {isAdded ? (
                                  <>
                                    <i className="bi bi-check"></i> เพิ่มแล้ว
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-plus-circle"></i> เพิ่ม
                                  </>
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted small">
                        แสดง {indexOfFirstMaterial + 1} - {Math.min(indexOfLastMaterial, filteredMaterials.length)} จาก {filteredMaterials.length} รายการ
                        {selectedCategory !== 'all' && <span className="ms-1">(กรองแล้ว)</span>}
                      </div>
                      <Pagination size="sm" className="mb-0">
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => handlePageChange(pageNumber)}
                              >
                                {pageNumber}
                              </Pagination.Item>
                            );
                          } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                            return <Pagination.Ellipsis key={pageNumber} disabled />;
                          }
                          return null;
                        })}

                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                      </Pagination>
                    </div>
                  )}
                </>
              )}

              <hr className="my-4" />
            </>
          )}

          {/* Selected Materials */}
          {selectedMaterials.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
              <p className="mt-2">ยังไม่มีวัตถุดิบในรายการ</p>
              <small>ค้นหาและเลือกวัตถุดิบเพื่อเพิ่มลงในรายการ</small>
            </div>
          ) : (
            <Table responsive>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '20%' }}>รหัส/ชื่อ</th>
                  <th style={{ width: '12%' }}>จำนวน</th>
                  <th style={{ width: '12%' }}>ราคา/หน่วย</th>
                  <th style={{ width: '10%' }} className="text-center">
                    <i className="bi bi-percent me-1"></i>
                    VAT
                  </th>
                  <th style={{ width: '12%' }}>รวม</th>
                  <th style={{ width: '18%' }}>หมายเหตุ</th>
                  <th style={{ width: '8%' }}>ลบ</th>
                </tr>
              </thead>
              <tbody>
                {selectedMaterials.map((item, index) => {
                  const subtotal = item.quantity * item.unitCost;
                  const vatAmount = item.hasVat ? (subtotal * vatRate / 100) : 0;
                  const total = subtotal + vatAmount;

                  return (
                    <tr key={index}>
                      <td>
                        <strong>{item.materialCode}</strong>
                        <br />
                        <small className="text-muted">{item.materialName}</small>
                        <br />
                        <small className="text-muted">({item.unit})</small>
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Button
                            variant="outline-secondary"
                            onClick={() => handleUpdateQuantity(index, Math.max(0.0001, item.quantity - 1))}
                            disabled={item.quantity <= 0.0001 || !allowQuantityEdit}
                          >
                            <i className="bi bi-dash"></i>
                          </Button>
                          <Form.Control
                            type="number"
                            min="0.0001"
                            step="0.0001"
                            value={item.quantity}
                            onChange={(e) => handleUpdateQuantity(index, e.target.value)}
                            className="text-center"
                            readOnly={!allowQuantityEdit}
                            disabled={!allowQuantityEdit}
                            title={!allowQuantityEdit ? 'ไม่สามารถแก้ไขจำนวนได้ (เบิกแล้ว)' : ''}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                            disabled={!allowQuantityEdit}
                          >
                            <i className="bi bi-plus"></i>
                          </Button>
                        </InputGroup>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitCost}
                          onChange={(e) => handleUpdateCost(index, e.target.value)}
                          readOnly={!allowPriceEdit}
                          disabled={!allowPriceEdit}
                          title={!allowPriceEdit ? 'ไม่สามารถแก้ไขราคาได้' : ''}
                        />
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={item.hasVat}
                          onChange={(e) => handleUpdateVat(index, e.target.checked)}
                          label={
                            <small className="text-muted">
                              {vatRate}%
                            </small>
                          }
                        />
                      </td>
                      <td>
                        <strong>{total.toFixed(2)} ฿</strong>
                        {item.hasVat && (
                          <>
                            <br />
                            <small className="text-success">
                              +VAT {vatAmount.toFixed(2)} ฿
                            </small>
                          </>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={item.note}
                          onChange={(e) => handleUpdateNote(index, e.target.value)}
                          placeholder="หมายเหตุ..."
                        />
                      </td>
                      <td>
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveMaterial(index)}
                          disabled={!allowQuantityEdit}
                          title={!allowQuantityEdit ? 'ไม่สามารถลบรายการได้ (เบิกแล้ว)' : ''}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Note and Summary */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <i className="bi bi-sticky me-2"></i>
            หมายเหตุและสรุป
          </h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>หมายเหตุ</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
            />
          </Form.Group>

          <hr />

          <div className="d-flex justify-content-between mb-2">
            <span>จำนวนรายการ:</span>
            <strong>{selectedMaterials.length} รายการ</strong>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span>จำนวนทั้งหมด:</span>
            <strong>
              {selectedMaterials.reduce((sum, item) => sum + item.quantity, 0)} ชิ้น
            </strong>
          </div>
          <hr />

          {/* Subtotals */}
          <div className="d-flex justify-content-between mb-2">
            <span>ยอดรวม (มี VAT):</span>
            <strong>
              {calculateSubtotalVat().toLocaleString('th-TH', {
                style: 'currency',
                currency: 'THB'
              })}
            </strong>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span>ยอดรวม (ไม่มี VAT):</span>
            <strong>
              {calculateSubtotalNonVat().toLocaleString('th-TH', {
                style: 'currency',
                currency: 'THB'
              })}
            </strong>
          </div>

          <hr />

          {/* Discount Inputs */}
          <Form.Group className="mb-3">
            <Form.Label>
              <i className="bi bi-percent me-1"></i>
              ส่วนลดสินค้ามี VAT
              <small className="text-muted ms-2">
                (สูงสุด {calculateSubtotalVat().toFixed(2)} ฿)
              </small>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="0"
                max={calculateSubtotalVat()}
                step="0.01"
                value={discountVat}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  const maxDiscount = calculateSubtotalVat();
                  if (value > maxDiscount) {
                    setDiscountVat(maxDiscount);
                    alert(`ส่วนลดสินค้ามี VAT ไม่สามารถเกินยอดรวมสินค้ามี VAT (${maxDiscount.toFixed(2)} ฿)`);
                  } else {
                    setDiscountVat(e.target.value);
                  }
                }}
                placeholder="0.00"
              />
              <InputGroup.Text>฿</InputGroup.Text>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              <i className="bi bi-percent me-1"></i>
              ส่วนลดสินค้าไม่มี VAT
              <small className="text-muted ms-2">
                (สูงสุด {calculateSubtotalNonVat().toFixed(2)} ฿)
              </small>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="0"
                max={calculateSubtotalNonVat()}
                step="0.01"
                value={discountNonVat}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  const maxDiscount = calculateSubtotalNonVat();
                  if (value > maxDiscount) {
                    setDiscountNonVat(maxDiscount);
                    alert(`ส่วนลดสินค้าไม่มี VAT ไม่สามารถเกินยอดรวมสินค้าไม่มี VAT (${maxDiscount.toFixed(2)} ฿)`);
                  } else {
                    setDiscountNonVat(e.target.value);
                  }
                }}
                placeholder="0.00"
              />
              <InputGroup.Text>฿</InputGroup.Text>
            </InputGroup>
          </Form.Group>

          {showMarkupInput && (
            <>
              <hr />
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="bi bi-graph-up-arrow me-1"></i>
                  บวกเพิ่ม (%)
                  <small className="text-muted ms-2">เพิ่มเปอร์เซ็นต์จากยอดรวมหลังหักส่วนลด</small>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={markupPercent}
                    onChange={(e) => setMarkupPercent(Math.max(0, parseFloat(e.target.value) || 0))}
                    placeholder="0"
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </>
          )}

          <hr />

          {/* Grand Total with Discounts */}
          <div className="d-flex justify-content-between mb-2">
            <span>ยอดรวม (มี VAT) หลังหักส่วนลด:</span>
            <strong className="text-success">
              {(() => {
                const vatSubtotal = calculateSubtotalVat();
                const clampedDiscount = Math.min(parseFloat(discountVat) || 0, vatSubtotal);
                return Math.max(0, vatSubtotal - clampedDiscount).toLocaleString('th-TH', {
                  style: 'currency',
                  currency: 'THB'
                });
              })()}
            </strong>
          </div>
          <div className="d-flex justify-content-between mb-3">
            <span>ยอดรวม (ไม่มี VAT) หลังหักส่วนลด:</span>
            <strong className="text-success">
              {(() => {
                const nonVatSubtotal = calculateSubtotalNonVat();
                const clampedDiscount = Math.min(parseFloat(discountNonVat) || 0, nonVatSubtotal);
                return Math.max(0, nonVatSubtotal - clampedDiscount).toLocaleString('th-TH', {
                  style: 'currency',
                  currency: 'THB'
                });
              })()}
            </strong>
          </div>

          <hr />

          {showMarkupInput && (parseFloat(markupPercent) || 0) > 0 && (() => {
            const vatSubtotal = calculateSubtotalVat();
            const nonVatSubtotal = calculateSubtotalNonVat();
            const clampedDiscountVat = Math.min(parseFloat(discountVat) || 0, vatSubtotal);
            const clampedDiscountNonVat = Math.min(parseFloat(discountNonVat) || 0, nonVatSubtotal);
            const baseTotal = Math.max(0, vatSubtotal - clampedDiscountVat) + Math.max(0, nonVatSubtotal - clampedDiscountNonVat);
            const markupAmount = baseTotal * (parseFloat(markupPercent) / 100);
            return (
              <div className="d-flex justify-content-between mb-2">
                <span className="text-info">บวกเพิ่ม {markupPercent}%:</span>
                <strong className="text-info">
                  +{markupAmount.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </strong>
              </div>
            );
          })()}

          <div className="d-flex justify-content-between mb-3">
            <h5>ยอดรวมทั้งสิ้น:</h5>
            <h5 className="text-primary">
              {calculateTotal().toLocaleString('th-TH', {
                style: 'currency',
                currency: 'THB'
              })}
            </h5>
          </div>

          {formError && (
            <Alert variant="danger" dismissible onClose={() => setFormError('')} className="mb-3">
              {formError}
            </Alert>
          )}

          <div className="d-grid gap-2">
            <Button
              type="submit"
              variant="success"
              size="lg"
              disabled={loading || selectedMaterials.length === 0}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  กำลังดำเนินการ...
                </>
              ) : (
                <>
                  <i className={`${submitButtonIcon} me-2`}></i>
                  {submitButtonText}
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default OrderForm;
